import { z } from 'zod';
import type { Permission } from './admin.js';

/**
 * F60b — admin sidebar badge counts (plans/F60b_badges_and_health_ui.md).
 *
 * One `GET /admin/nav-counts` returns every badge the admin may see, so the
 * console polls once a minute per visible tab instead of once per badge. The
 * route is not audited (it is polled) and caches each count for 15 s.
 */

export const ADMIN_NAV_COUNT_KEYS = [
  'verifications',
  'venueSubmissions',
  'venueEdits',
  'venueClaims',
  'venuePhotos',
  'paymentDisputes',
  'commissionStatements',
  'whatsappTemplates',
  'skillDisputes',
  'openGameReports',
  'trainerVerifications',
  'flaggedReviews',
  'placementApprovals',
  'suspendedPromotions',
  'suspendedTournaments',
  'flaggedVenues',
  'openReports',
  'pendingGdpr',
  'financeReconciliation',
] as const;
export type AdminNavCountKey = (typeof ADMIN_NAV_COUNT_KEYS)[number];

/**
 * The permission each badge needs: the same permission that gates the queue it
 * counts. The API returns only the keys the admin holds; the sidebar shows a
 * badge only for those keys. `whatsappTemplates` additionally needs the
 * WhatsApp feature to be live (`GET /auth/config` → `features.whatsapp`).
 */
export const ADMIN_NAV_COUNT_PERMISSIONS: Readonly<Record<AdminNavCountKey, Permission>> = {
  verifications: 'verifications:read',
  venueSubmissions: 'venues:moderate',
  venueEdits: 'venues:moderate',
  venueClaims: 'venue_claims:read',
  venuePhotos: 'venue_photos:moderate',
  paymentDisputes: 'payment_disputes:read',
  commissionStatements: 'payments:read',
  whatsappTemplates: 'whatsapp:read',
  skillDisputes: 'skill_disputes:resolve',
  openGameReports: 'open_games:read',
  trainerVerifications: 'trainer_verifications:review',
  flaggedReviews: 'reviews:moderate',
  placementApprovals: 'placement_approvals:review',
  suspendedPromotions: 'promotions:read',
  suspendedTournaments: 'tournaments:read',
  flaggedVenues: 'venues:read',
  openReports: 'reports:read',
  pendingGdpr: 'gdpr_requests:read',
  financeReconciliation: 'financial:read',
};

/** A key is absent when the admin lacks its permission (or its count failed). */
export type AdminNavCounts = Partial<Record<AdminNavCountKey, number>>;

/** `fresh=1` skips the 15 s cache — sent once right after an admin action. */
export const AdminNavCountsQuerySchema = z.object({ fresh: z.enum(['1']).optional() }).strict();
export type AdminNavCountsQuery = z.infer<typeof AdminNavCountsQuerySchema>;
